/**
 * QuotePipelineBoard — S30. Sales lead pipeline over every quote submission
 * (Multifamily + Commercial). Four stages: New → Contacted → Proposal Sent →
 * Won. Drag a card between columns (or use the stage select). New,
 * unacknowledged submissions render red until acknowledged.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { KanbanSquare, RefreshCw, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';
const STAGES = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: '#a855f7' },
  { key: 'won', label: 'Won', color: '#10b981' },
];

function monthlyValue(q) {
  const rng = q.estimate_range;
  if (!rng) return null;
  const mid = (Number(rng.low) + Number(rng.high)) / 2;
  if (q.kind === 'commercial') return Math.round(mid);
  return Math.round(mid * (q.unit_count || 1));
}

export default function QuotePipelineBoard() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [manager, setManager] = useState('all');
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/service-packages/quotes');
      setSubs(data.submissions || []);
    } catch (_) { /* non-admin */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const managers = useMemo(() => {
    const s = new Set();
    subs.forEach((q) => q.account_manager && s.add(q.account_manager));
    return [...s].sort();
  }, [subs]);

  const filtered = useMemo(() => subs.filter((q) => {
    const qKind = q.kind === 'commercial' ? 'commercial' : 'multifamily';
    if (kind !== 'all' && qKind !== kind) return false;
    if (manager !== 'all' && q.account_manager !== manager) return false;
    const d = (q.submitted_at || '').slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  }), [subs, kind, manager, dateFrom, dateTo]);

  const byStage = useMemo(() => {
    const m = { new: [], contacted: [], proposal_sent: [], won: [] };
    filtered.forEach((q) => { (m[q.pipeline_stage || 'new'] || m.new).push(q); });
    return m;
  }, [filtered]);

  const moveStage = async (submissionId, stage) => {
    const q = subs.find((s) => s.submission_id === submissionId);
    if (!q || (q.pipeline_stage || 'new') === stage) return;
    setSubs((prev) => prev.map((s) => s.submission_id === submissionId ? { ...s, pipeline_stage: stage } : s));
    try {
      const res = await apiFetch(`/api/service-packages/quotes/${submissionId}/stage`, { method: 'PATCH', body: { stage } });
      if (stage === 'won' && res.handoff) {
        toast.success('Won! Draft contract created for Account Management · Operations notified to schedule onboarding');
      } else {
        toast.success(`Moved to ${STAGES.find((s) => s.key === stage)?.label}`);
      }
      load();
    } catch (e) { toast.error(e.detail || 'Stage update failed'); load(); }
  };

  const acknowledge = async (submissionId) => {
    try {
      await apiFetch(`/api/service-packages/quotes/${submissionId}/review`, { method: 'POST' });
      toast.success('Acknowledged');
      load();
    } catch (e) { toast.error(e.detail || 'Action failed'); }
  };

  if (loading) return null;

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid="quote-pipeline-board">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <KanbanSquare className="h-4 w-4" /> Lead Status Pipeline · Quotes
        </h2>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Quote Type</p>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="pipeline-filter-kind">
              <option value="all">All</option>
              <option value="multifamily">Multifamily</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">From</p>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="pipeline-filter-from" />
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">To</p>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="pipeline-filter-to" />
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Account Manager</p>
            <select value={manager} onChange={(e) => setManager(e.target.value)}
              className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="pipeline-filter-manager">
              <option value="all">All</option>
              {managers.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white" data-testid="pipeline-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {STAGES.map((st) => (
          <div key={st.key}
            onDragOver={(e) => { e.preventDefault(); setOverStage(st.key); }}
            onDragLeave={() => setOverStage((s) => (s === st.key ? null : s))}
            onDrop={(e) => { e.preventDefault(); setOverStage(null); if (dragId) moveStage(dragId, st.key); setDragId(null); }}
            className={`rounded-lg border p-2.5 min-h-[180px] transition-colors ${overStage === st.key ? 'border-[#C9A962] bg-[#C9A962]/5' : 'border-zinc-700 bg-zinc-800/30'}`}
            data-testid={`pipeline-col-${st.key}`}>
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: st.color }}>{st.label}</span>
              <span className="text-[11px] font-bold text-zinc-400" data-testid={`pipeline-count-${st.key}`}>{byStage[st.key].length}</span>
            </div>
            <div className="space-y-2">
              {byStage[st.key].length === 0 && (
                <p className="text-[11px] text-zinc-600 text-center py-4">Drop leads here</p>
              )}
              {byStage[st.key].map((q) => {
                const unread = q.status === 'new';
                const val = monthlyValue(q);
                const bType = q.kind === 'commercial'
                  ? (q.facility_profile?.business_type || 'commercial')
                  : 'multifamily';
                return (
                  <div key={q.submission_id}
                    draggable
                    onDragStart={() => setDragId(q.submission_id)}
                    onDragEnd={() => setDragId(null)}
                    className={`rounded-md border p-2.5 cursor-grab active:cursor-grabbing ${unread ? 'border-red-500 bg-red-950/40' : 'border-zinc-700 bg-zinc-900'}`}
                    data-testid={`pipeline-card-${q.submission_id}`} data-stage={q.pipeline_stage || 'new'}>
                    <div className="flex items-start justify-between gap-1">
                      <p className={`text-xs font-bold leading-tight ${unread ? 'text-red-300' : 'text-white'}`}>{q.full_name}</p>
                      {unread && <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-black uppercase shrink-0">New</span>}
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{q.property_name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] uppercase tracking-wider font-bold" style={{ color: GOLD }}>{bType}</span>
                      <span className="text-[11px] font-bold text-emerald-400" data-testid={`pipeline-value-${q.submission_id}`}>
                        {val !== null ? `~$${val.toLocaleString()}/mo` : 'Review req.'}
                      </span>
                    </div>
                    {q.account_manager && (
                      <p className="text-[9px] text-zinc-500 mt-1">AM: {q.account_manager}</p>
                    )}
                    <div className="flex items-center gap-1 mt-1.5">
                      <select value={q.pipeline_stage || 'new'}
                        onChange={(e) => moveStage(q.submission_id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 rounded bg-zinc-800 border border-zinc-700 px-1 py-0.5 text-[10px] text-zinc-300"
                        data-testid={`pipeline-stage-select-${q.submission_id}`}>
                        {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                      {unread && (
                        <button onClick={() => acknowledge(q.submission_id)}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#C9A962] text-black text-[9px] font-black uppercase"
                          title="Acknowledge — clears the red new-lead alert"
                          data-testid={`pipeline-ack-${q.submission_id}`}>
                          <CheckCircle2 className="h-2.5 w-2.5" /> Ack
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 mt-3">
        Drag a card between columns to update its stage. New submissions auto-enter at New and stay red until acknowledged. Sending a proposal from a quote auto-advances it to Proposal Sent.
      </p>
    </section>
  );
}
